'use client';
import React, { FC, useEffect, useState } from 'react';
import { TypeInverseTextHeroFields } from '@/core/generated-types';
import { Badge } from '@/components';
import { UniversalCta } from '../UniversalCta';
import useAuth from '@/context/AuthContext';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';
import styles from './InverseTextHero.module.scss';
import { RichTextHeading } from '../RichTextHeading';

const InverseTextHero: FC<TypeInverseTextHeroFields> = ({
  leadingText,
  mainText,
  mainRichText,
  cta,
  trailingText,
  backgroundColor,
  badge,
  descriptionTextLoggedOut,
  descriptionTextLoggedIn,
}) => {
  const [hydrated, setHydrated] = useState(false);
  const { user } = useAuth();
  const useLoggedInText = !!user;
  const textToDisplay = useLoggedInText
    ? descriptionTextLoggedIn
    : descriptionTextLoggedOut;

  // This hydrated state is needed to avoid hydration errors due to SSR https://nextjs.org/docs/messages/react-hydration-error#solution-1-using-useeffect-to-run-on-the-client-only
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div className={`px-6 py-10 lg:py-12 bg-${backgroundColor?.fields?.color}`}>
      <div className="mx-auto max-w-[740px] text-center">
        {badge && (
          <div className="mb-3 lg:mb-4">
            <Badge {...badge.fields} />
          </div>
        )}
        {leadingText && (
          <div
            className="mb-2 text-lg font-bold md:text-2xl"
            data-testid="leading-text"
          >
            {leadingText}
          </div>
        )}
        {mainText && (
          <div
            className="text-[32px] font-bold leading-[40px] md:text-[42px] md:leading-[56px]"
            data-testid="main-text"
          >
            {mainText}
          </div>
        )}
        {mainRichText && <RichTextHeading content={mainRichText as Document} />}
        {cta && <UniversalCta link={cta} className="mt-6 inline-flex" />}
        {textToDisplay && hydrated && (
          <div className={`${styles.inverseTextHero} text-sm mt-4`}>
            <RichText content={textToDisplay as Document} />
          </div>
        )}
        {trailingText && hydrated && (
          <div className="mt-6 text-sm">
            <RichText content={trailingText as Document} />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(InverseTextHero);
